import { CodeEditor } from '@acrodata/code-editor';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy,Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { languages } from '@codemirror/language-data';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AdminComponentBase, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { PromptDialogService } from 'app/shared/dialog/prompt-dialog.service';
import { blobToBase64 } from 'app/shared/utilities/file-helpers';
import { InteractDirective } from 'app/shared/utilities/interact.directive';

import { MediaclipError } from './mediaclip-error';

export type EditorFileInformation = {
    filename: string;
    mimeType: string;
    url: string;
};

export type UploadAssetEditor =
    {
        filename: string;
    }
    & (
    {
        content: string;
    }
    |
    {
        contentBase64: string;
    }
    );

export type DeleteAssetEditorFile = {
    filename: string;
    version: string;
};

export type AssetEditorFileCustomAction = {
    name: string;
    label: string;
    icon: string;
}

export type AssetCustomActionEvent<TFile extends EditorFileInformation> = {
    actionName: string;
    file: TFile;
}

@Component({
    selector: 'admin-assets-editor',
    templateUrl: './assets-editor.component.html',
    styleUrls: ['./assets-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ErrorComponent, MatButton, MatIconButton, MatIcon, SaveButtonComponent, MatNavList, MatListItem, FormsModule, CodeEditor, InteractDirective, MatFormFieldModule, MatSelectModule, MatOptionModule, MatMenuTrigger, MatMenu, MatMenuItem, MatTooltip],
})
export class AssetsEditorComponent<TFile extends EditorFileInformation> extends AdminComponentBase implements OnDestroy {
    private readonly promptDialogService = inject(PromptDialogService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly httpClient = inject(HttpClient);

    @Input()
    assetEditorInformation: TFile[];
    @Input()
    acceptedUploadFileTypes: string[] = [];
    @Input()
    fileCustomActions: AssetEditorFileCustomAction[] = [];
    @Output()
    uploadAsset = new EventEmitter<UploadAssetEditor>();
    @Output()
    uploadAssets = new EventEmitter<UploadAssetEditor[]>();
    @Output()
    deleteAsset = new EventEmitter<TFile>();
    @Output()
    customAction = new EventEmitter<AssetCustomActionEvent<TFile>>();

    @ViewChild('singleFileUploader') singleFileUploader: ElementRef;
    @ViewChild('multipleFilesUploader') multipleFilesUploader: ElementRef;


    readonly languages = languages;
    selectedFile?: TFile;
    savedFileContents: Record<string, string> = {};
    fileContents: Record<string, string> = {};
    fileMimeTypes: Record<string, string> = {};
    menuFileContext?: EditorFileInformation;

    constructor() {
        super();
        this.pageStatus = PageStatus.loaded;
    }

    selectFile(file?: TFile) {
        this.selectedFile = file;
        if (!file) {
            return;
        }
        if (!(file.filename in this.fileContents)) {
            this.subscribeWithGenericLoadingErrorHandling(this.getAssetFile(file.url), ({ content, mimeType }) => {
                this.fileContents[file.filename] = content;
                this.savedFileContents[file.filename] = content;
                this.fileMimeTypes[file.filename] = mimeType || 'application/octet-stream';
            });
        }
    }

    getAssetFile(url: string): Observable<{ content: string, mimeType: string | null }> {
        return this.httpClient
            .get(url, {
                observe: 'response',
                responseType: 'text',
            })
            .pipe(
                catchError((err) => {
                    if (err instanceof HttpErrorResponse && err.status === 404) {
                        return of('');
                    }
                    throw new MediaclipError(`Unable to load generated image asset file at ${url}`, { innerError: err });
                }),
                map((response) => {
                    if (typeof response === 'string') {
                        throw new Error('Not supported response of type string in getEditorFile');
                    }
                    return {
                        content: response.body || '',
                        mimeType: response.headers.get('Content-Type'),
                    };
                }),
            );
    }


    isTextEditableFile(file: EditorFileInformation) {
        return file.mimeType.startsWith('text/') || file.mimeType.startsWith('application/json') || file.mimeType.startsWith('application/javascript');
    }

    isImage(file: EditorFileInformation) {
        return file.mimeType.startsWith('image/');
    }

    isFileNotSaved(): boolean {
        for (let key of Object.keys(this.fileContents)) {
            if (this.fileContents[key] !== this.savedFileContents[key]) {
                return true;
            }
        }
        return false;
    }

    save(file: EditorFileInformation) {
        this.uploadAsset.emit({
            filename: file.filename,
            content: this.fileContents[file.filename],
        });
    }

    saveAll() {
        let modifiedFiles = this.assetEditorInformation.filter(f => this.isFileModified(f));
        if (modifiedFiles.length == 0) {
            return;
        }
        this.uploadAssets.emit(modifiedFiles.map(f => ({
            filename: f.filename,
            content: this.fileContents[f.filename],
        })));
    }


    async uploadFromFiles(files: FileList) {
        if (!files || files.length === 0) {
            return;
        }

        let newFiles = [];
        for (let file of Array.from(files)) {
            newFiles.push({
                filename: file.name,
                contentBase64: await blobToBase64(file),
            });
        }

        this.uploadAssets.emit(newFiles);
        this.multipleFilesUploader.nativeElement.value = null;
    }


    async uploadSelectedFileContent(filename: string, files: FileList) {
        if (files.length < 1) {
            return;
        }
        let file = files[0];
        const contentBase64 = await blobToBase64(file);
        this.uploadAsset.emit({
            filename: filename,
            contentBase64: contentBase64,
        });
        this.singleFileUploader.nativeElement.value = null;
    }

    updateFile(uploadedFile: TFile) {
        this.savedFileContents[uploadedFile.filename] = this.fileContents[uploadedFile.filename];
        if (this.selectedFile?.filename !== uploadedFile.filename) {
            this.selectFile(uploadedFile);
        }
    }

    addNewFile() {
        let defaultValue = 'index.html';
        if (this.assetEditorInformation.find(x => x.filename === defaultValue)) {
            defaultValue = '';
        }
        this.promptDialogService.prompt('Add new file', undefined, { fieldLabel: 'Filename', defaultValue: defaultValue }).subscribe((filename) => {
            if (!filename) {
                return;
            }
            this.uploadAsset.emit({
                filename: filename,
                content: '',
            });
        });

    }

    deleteFile(file: TFile) {
        this.confirmDialogService.confirm(
            'Delete file',
            `Do you want to delete the file <code>${file.filename}</code>?`,
            undefined,
            {
                yes: {
                    label: 'Delete',
                    warn: true,
                },
                no: {
                    label: 'Cancel',
                },
            },
        )
            .subscribe(() => {
                let index = this.assetEditorInformation.findIndex(f => f.filename === file.filename);
                if (index > 0) {
                    this.selectFile(this.assetEditorInformation[index - 1]);
                } else if (index == 0) {
                    this.selectFile(this.assetEditorInformation[1]);
                }
                this.deleteAsset.emit(file);
            });
    }

    isFileModified(file: EditorFileInformation): boolean {
        return this.savedFileContents[file.filename] !== this.fileContents[file.filename];
    }

    getLanguage(fileMimeType: string) {
        if (fileMimeType === 'application/json') {
            return 'json';
        }
        if (fileMimeType === 'application/javascript') {
            return 'javascript';
        }
        if (fileMimeType === 'text/html') {
            return 'html';
        }
        if (fileMimeType === 'text/css') {
            return 'css';
        }
        return '';
    }

    handleSaveEditor(event: KeyboardEvent, file: EditorFileInformation) {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault(); // Prevent browser's save dialog
            this.save(file);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
    }
}
